import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';

const locationOptions = {
  IT: {
    Bengaluru: ['Centennial 2', 'Centennial 3', 'Ecospace 3B', 'Ecoworld', 'Ecoworld 4D', 'New Indiranagar', 'Residency Road', 'Whitefield', 'Yelahanka'],
    Mumbai: ['BKC, Equinox T3', 'BKC, Equinox T3, 7th Floor', 'Powai, Spectra', 'Powai, Prudential', 'Powai, Winchester', 'Worli'],
    Delhi: ['Aerocity', 'Cybercity', 'Golf Course Road', 'Gurugram, Sector 21', 'Noida, Sector 135', 'Noida, Sector 62', 'Gurugram, Sector 48'],
    Chennai: ['OMR', 'OMR 3B 5F', 'Perungudi'],
    Hyderabad: ['Sandhya Techno', 'Skyview'],
    Pune: ['Baner'],
    Vizag: ['APIS IT Incubation Centre'],
    AtelierSuites: ['Aerocity, Worldmark 3'],
  },
  'Non-IT': {
    Bengaluru: ['Centennial 2', 'Centennial 3', 'Ecospace 3B', 'Ecoworld', 'Ecoworld 4D', 'New Indiranagar', 'Residency Road', 'Whitefield', 'Yelahanka'],
    Mumbai: ['BKC, Equinox T3', 'BKC, Equinox T3, 7th Floor', 'Powai, Spectra', 'Powai, Prudential', 'Powai, Winchester', 'Worli'],
    Delhi: ['Aerocity', 'Cybercity', 'Golf Course Road', 'Gurugram, Sector 21', 'Noida, Sector 135', 'Noida, Sector 62', 'Gurugram, Sector 48'],
    Chennai: ['OMR', 'OMR 3B 5F', 'Perungudi'],
    Hyderabad: ['Sandhya Techno', 'Skyview'],
    Pune: ['Baner'],
    Vizag: ['APIS IT Incubation Centre'],
    AtelierSuites: ['Aerocity, Worldmark 3'],
  },
};

export default function JobFilter({ onFilterChange }) {
  const [type, setType] = useState('');
  const [mainLocation, setMainLocation] = useState('');
  const [subLocation, setSubLocation] = useState('');
  const [search, setSearch] = useState('');
  const [showLocationMenu, setShowLocationMenu] = useState(false);

  const [errorFields, setErrorFields] = useState({
    type: false,
    location: false,
    search: false,
  });

  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowLocationMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resetAll = () => {
    setType('');
    setMainLocation('');
    setSubLocation('');
    setSearch('');
    setErrorFields({ type: false, location: false, search: false });
    onFilterChange({ type: '', location: '', search: '' });
  };

  const handleSearch = () => {
    const missingType = !type;
    const missingLocation = !mainLocation || !subLocation;
    const missingSearch = !search.trim();

    const newErrors = {
      type: missingType,
      location: missingLocation,
      search: missingSearch,
    };
    setErrorFields(newErrors);

    if (missingType || missingLocation || missingSearch) return;

    const location = `${subLocation}, ${mainLocation}`;
    onFilterChange({ type, location, search });
  };

  const handleSubLocationClick = (subloc, city) => {
    setMainLocation(city);
    setSubLocation(subloc);
    setShowLocationMenu(false);
  };

  return (
    <div className="w-full px-4 sm:px-6 md:px-10 py-6 md:py-8 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl shadow-lg border border-blue-200">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Job Category + Location */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

          {/* Job Category */}
          <div>
            <label className="block font-semibold text-blue-800 mb-1">Job Category</label>
            <select
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setMainLocation('');
                setSubLocation('');
                setErrorFields((prev) => ({ ...prev, type: false }));
              }}
              className={`w-full border-2 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 bg-white text-gray-800 ${
                errorFields.type ? 'border-red-500 ring-red-300' : 'border-blue-300 focus:ring-blue-400'
              }`}
            >
              <option value="">Select</option>
              <option value="IT">IT</option>
              <option value="Non-IT">Non-IT</option>
            </select>
            {errorFields.type && (
              <p className="mt-1 text-xs text-red-600">Please select job category</p>
            )}
          </div>

          {/* Location */}
          <div className="relative" ref={menuRef}>
            <label className="block font-semibold text-blue-800 mb-1">Location</label>
            <div
              className={`w-full border-2 px-3 py-2 rounded-lg bg-white text-gray-800 cursor-pointer hover:bg-blue-50 ${
                errorFields.location ? 'border-red-500' : 'border-blue-300'
              }`}
              onClick={() => setShowLocationMenu((prev) => !prev)}
            >
              {subLocation && mainLocation
                ? `${subLocation}, ${mainLocation}`
                : 'Select Location'}
            </div>
            {errorFields.location && (
              <p className="mt-1 text-xs text-red-600">Please select location</p>
            )}

            {showLocationMenu && type && (
              <div className="absolute top-full mt-1 left-0 bg-white border border-blue-300 shadow-lg rounded-md z-50 flex flex-col sm:flex-row w-full sm:w-auto">

                {/* Main Cities */}
                <div className="min-w-[150px] border-b sm:border-b-0 sm:border-r border-gray-200 max-h-48 sm:max-h-64 overflow-y-auto">
                  {Object.keys(locationOptions[type]).map((city) => (
                    <div
                      key={city}
                      onMouseEnter={() => setMainLocation(city)}
                      className="flex justify-between items-center px-3 py-2 hover:bg-blue-100 text-sm cursor-pointer"
                    >
                      {city}
                      <ChevronRight className="w-4 h-4 text-gray-500 ml-1 hidden sm:inline" />
                    </div>
                  ))}
                </div>

                {/* Sub Locations */}
                {mainLocation && locationOptions[type][mainLocation] && (
                  <div className="min-w-[180px] max-h-48 sm:max-h-64 overflow-y-auto">
                {locationOptions[type][mainLocation].map((subloc) => (
                  <div
                    key={subloc}
                    onClick={() => {
                      handleSubLocationClick(subloc, mainLocation);
                      setErrorFields((prev) => ({ ...prev, location: false }));
                    }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-100 ${
                      subloc === subLocation ? 'bg-blue-200 font-semibold text-blue-800' : ''
                    }`}
                  >
                    {subloc}
                  </div>
                ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Search and Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
          <div className="sm:col-span-2">
            <label className="block font-semibold text-blue-800 mb-1">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setErrorFields((prev) => ({ ...prev, search: false }));
              }}
              placeholder="Search by job title"
              className={`w-full border-2 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 bg-white text-gray-800 ${
                errorFields.search ? 'border-red-500 ring-red-300' : 'border-blue-300 focus:ring-blue-400'
              }`}
            />
            {errorFields.search && (
              <p className="mt-1 text-xs text-red-600">Please enter search text</p>
            )}
          </div>

          <div className="flex gap-2 sm:pt-7">
            <button
              onClick={handleSearch}
              className="flex-1 h-10 px-4 bg-blue-600 text-white rounded border border-blue-600 hover:bg-blue-700 transition text-sm"
            >
              Search
            </button>
            <button
              onClick={resetAll}
              className="flex-1 h-10 px-4 bg-gray-100 border border-blue-600 text-blue-600 rounded hover:bg-blue-600 hover:text-white transition text-sm"
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
